declare const brandSym: unique symbol
declare const typeSym: unique symbol
type Brand<T, B extends symbol> = {[brandSym]: B; [typeSym]: T}

/**
 * Given a type T, returns a new type that "brands" T with extra type information.
 * This replicates making a "newtype" around T.
 *
 * Example (these examples are also in the tests):
 *
 *     const specialStringSymbol = Symbol('special string!')
 *     type TSpecialString = Branded<string, typeof specialStringSymbol>
 *
 * With the above example, you now have a `TSpecialString` type that can be used
 * where `string` is expected, but cannot be set to a value of the "raw"/unbranded
 * string type.
 *
 *     const regularString = 'foo'
 *     const specialString = brand<TSpecialString>('foo')
 *
 *     console.log(regularString === specialString) // true - at runtime, they are the same string value
 *
 *     const stringFunc = (input: string) => undefined
 *     stringFunc(regularString) // OK
 *     stringFunc(specialString) // OK
 *
 * However, you can't accidentally use one branded type where another is expected
 *
 *     const exquisiteStringSymbol = Symbol('exquisite string!')
 *     type TExquisiteString = Branded<string, typeof exquisiteStringSymbol>
 *     const exquisiteString = brand<TExquisiteString>('foo')
 *
 *     const exquisiteFunc = (input: TExquisiteString) => undefined
 *     exquisiteFunc(exquisiteString) // OK
 *
 *     // error: Type `typeof specialStringSymbol` is not assignable to type `typeof exquisiteStringSymbol`
 *     exquisiteFunc(specialString)
 *     // error: Type string is not assignable to type Brand<string, unique symbol>
 *     exquisiteFunc(regularString)
 */
export type Branded<T, B extends symbol> = T & Brand<T, B>

/**
 * Returns the original type that was `Branded`.
 *
 * For example, using the types from `Branded`'s examples:
 *
 *     type TJustAString = Unbranded<TSpecialString> // just `string`
 *     const justAString: TJustAString = 'foo' // ok
 */
export type Unbranded<BrandedType extends Branded<unknown, symbol>> =
  BrandedType extends Branded<infer T, symbol> ? T : never

/**
 * Assigns the given branded type to a runtime value of the unbranded type.
 * In other words, this is how you "construct" a branded value.
 *
 * > Note that this only has an effect at the type level
 * > (at runtime, this function is just the identity).
 *
 * Example:
 *
 *     const specialString = brand<TSpecialString('foo')>
 */
export const brand = <BrandedType extends Branded<unknown, symbol>>(
  t: Unbranded<BrandedType>
) => t as BrandedType

/**
 * Removes the branding type wrapper off of a given branded value.
 * In other words, this is how you "unwrap" a branded value.
 *
 * > Note that this only has an effect at the type level
 * > (at runtime, this function is just the identity).
 *
 * Example:
 *
 *     const justString: string = unbrand<specialString>
 */
export const unbrand = <BrandedType extends Branded<unknown, symbol>>(
  t: BrandedType
) => t as Unbranded<BrandedType>

/**
 * Removes brand wrappers from the given object type's values (if applicable)
 * NB: Makes changes at the top-level only for now.
 */
export type UnbrandedObject<Obj> = {
  [T in keyof Obj]: Obj[T] extends Branded<unknown, symbol>
    ? Unbranded<Obj[T]>
    : Obj[T]
}
